import React, { useState, useEffect } from 'react';
import styles from '../NeedInfoV2.module.scss'; // Reuse existing styles
import { Input } from '../../_custom';
import Button from '../../_custom/Button/Button';
import { EntityRequirement } from '../CustomHooks/useFetchRequirements';
import { useAppSelector } from '../../../app/hooks';
import { Mixpanel } from '../../../utils/mixpanel';
import { getSubdomain } from '../../../utils/utils';
import { AICA_DATA_PENDING_ADD_ITEM } from '../../../utils/constants/mixpanelEvents/investorEvents';
import { SOURCE } from '../NeedInfoV2Constants';
import { useSearchParams } from 'react-router-dom';

interface AddClarificationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (clarification: string) => void; // Submit takes the single clarification string
    isEditing?: boolean;
    editedEntity?: EntityRequirement | null;
    handleEdit?: (id: string, updates: Partial<EntityRequirement>) => Promise<void>; // Optional edit function
    organizationId: string;
}

const AddClarificationDrawer: React.FC<AddClarificationDrawerProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isEditing,
    editedEntity,
    handleEdit,
    organizationId,
}) => {
    const [clarificationText, setClarificationText] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const user = useAppSelector((state) => state.user.userData);
    const [searchParams] = useSearchParams();

    // Effect no longer depends on requirement
    useEffect(() => {
        if (!isOpen) {
            setClarificationText(''); // Clear on close
        } else if (isEditing && editedEntity) {
            setClarificationText(editedEntity.primaryMessage ?? '');
        }
    }, [isOpen, editedEntity]);

    const handleTextChange = (value: string) => {
        setClarificationText(value);
    };

    const handleSubmit = async () => {
        const trimmedText = clarificationText.trim();
        if (!trimmedText) return;

        const clarificationItems = trimmedText
            .split('\n')
            .map((item) => item.trim())
            .filter((item) => item !== '');
        if (clarificationItems.length === 0) return;

        setIsSubmitting(true);
        onSubmit(clarificationItems.join('\n'));
        setIsSubmitting(false);

        Mixpanel.track(AICA_DATA_PENDING_ADD_ITEM, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId,
        });
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`${styles.Backdrop} ${isOpen ? styles.BackdropVisible : ''}`}
                onClick={onClose}
                style={{ zIndex: '1001' }}
                role="presentation"
                aria-hidden={!isOpen}
            />
            {/* Drawer */}
            <div
                className={`${styles.Drawer} ${isOpen ? styles.Open : styles.Closed}`}
                style={{ height: 'auto', minHeight: '300px' }} // Adjust height as needed
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-clarification-drawer-title"
            >
                {/* Header */}
                <div className={styles.Header}>
                    <h2 id="add-clarification-drawer-title" className={styles.HeaderText}>
                        {searchParams.get(SOURCE.BORROWER_REQ) === 'true' ? 'Add Clarification' : 'Add Query'}
                    </h2>
                    <button onClick={onClose} className={styles.CloseButton} aria-label="Close drawer">
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className={styles.Content} style={{ padding: '1rem' }}>
                    <Input
                        style={{ marginTop: '0.5rem' }}
                        type="textarea"
                        placeholder="Enter details for new queries..."
                        value={clarificationText}
                        onChange={handleTextChange}
                        textAreaRows={8} // Adjust rows as needed
                        allowAllCharacters={true}
                    />
                </div>

                {/* Footer */}
                <div
                    style={{
                        borderTop: '1px solid #e0e0e0',
                        padding: '1rem',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1rem',
                        marginTop: 'auto', // Push footer to bottom
                    }}
                >
                    <Button text="Cancel" onClick={onClose} variant="secondary" style={{ minWidth: '100px', margin: 0 }} />
                    <Button
                        text={isSubmitting ? 'Adding...' : 'Add Item'}
                        onClick={() => {
                            if (isEditing) handleEdit?.(editedEntity?.id as string, { originalQuery: clarificationText });
                            else handleSubmit();
                            setClarificationText('');
                            onClose();
                        }}
                        variant="primary"
                        style={{ minWidth: '150px', margin: 0 }}
                        disabled={!clarificationText.trim() || isSubmitting}
                    />
                </div>
            </div>
        </>
    );
};

export default AddClarificationDrawer;
